/**
 * Cookie consent management utilities
 * Handles storing and retrieving user consent preferences
 */

export type CookieConsentPreferences = {
  essential: true // Always true, cannot be disabled
  functional: boolean
  analytics: boolean
  consented: boolean // Whether the user has made a choice
  timestamp: number
}

const CONSENT_COOKIE_NAME = 'cookie_consent'

const DEFAULT_PREFERENCES: CookieConsentPreferences = {
  essential: true,
  functional: false,
  analytics: false,
  consented: false,
  timestamp: 0
}

/**
 * Get cookie consent preferences from cookies (client-side)
 */
export function getConsentPreferences(): CookieConsentPreferences {
  if (typeof document === 'undefined') {
    return DEFAULT_PREFERENCES
  }

  try {
    const cookie = document.cookie.split('; ').find(row => row.startsWith(`${CONSENT_COOKIE_NAME}=`))

    if (!cookie) {
      return DEFAULT_PREFERENCES
    }

    const value = decodeURIComponent(cookie.split('=')[1])
    const parsed = JSON.parse(value)

    // Validate structure
    if (typeof parsed !== 'object' || parsed.essential !== true) {
      return DEFAULT_PREFERENCES
    }

    return {
      essential: true,
      functional: Boolean(parsed.functional),
      analytics: Boolean(parsed.analytics),
      consented: Boolean(parsed.consented),
      timestamp: parsed.timestamp || 0
    }
  } catch {
    return DEFAULT_PREFERENCES
  }
}

/**
 * Save cookie consent preferences (client-side)
 */
export function saveConsentPreferences(preferences: Omit<CookieConsentPreferences, 'essential' | 'consented' | 'timestamp'>): void {
  if (typeof document === 'undefined') return

  const fullPreferences: CookieConsentPreferences = {
    essential: true,
    functional: preferences.functional,
    analytics: preferences.analytics,
    consented: true,
    timestamp: Date.now()
  }

  // Set cookie for 1 year
  const expires = new Date()
  expires.setFullYear(expires.getFullYear() + 1)

  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(fullPreferences))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`

  // Dispatch custom event so other components can react
  window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: fullPreferences }))
}

/**
 * Check if user has consented to a specific cookie category
 */
export function hasConsent(category: keyof Omit<CookieConsentPreferences, 'consented' | 'timestamp'>): boolean {
  const preferences = getConsentPreferences()
  return preferences.consented && preferences[category]
}

/**
 * Accept all cookies
 */
export function acceptAllCookies(): void {
  saveConsentPreferences({
    functional: true,
    analytics: true
  })
}

/**
 * Reject all non-essential cookies
 */
export function rejectAllCookies(): void {
  saveConsentPreferences({
    functional: false,
    analytics: false
  })
}
